import React, { memo } from "react";
import { Handle, Node, NodeProps, Position } from "@xyflow/react";
import { BlueprintModule, ExecutionStep, ModuleDefinition } from "../types";
import { moduleColor, moduleIcon } from "../moduleStyle";

export interface ModuleRunSummary {
    operations: number;
    status: 'done' | 'error' | 'handled' | 'waiting' | 'running';
    steps: ExecutionStep[];
}

export type ModuleNodeData = {
    module: BlueprintModule;
    definition?: ModuleDefinition;
    hasError: boolean;
    hasWarning: boolean;
    run?: ModuleRunSummary;
};

export type ModuleFlowNode = Node<ModuleNodeData, 'module'>;

const RUN_COLORS: Record<ModuleRunSummary['status'], string> = {
    done: '#0ab39c',
    handled: '#f7b84b',
    waiting: '#299cdb',
    running: '#299cdb',
    error: '#f06548',
};

/** A module on the canvas, drawn like Make's: a coloured circle with its number. */
const ModuleNode = ({ data, selected }: NodeProps<ModuleFlowNode>) => {
    const { module, definition, hasError, hasWarning, run } = data;
    const color = moduleColor(module.module);
    const ring = hasError ? '#f06548' : hasWarning ? '#f7b84b' : selected ? '#405189' : 'transparent';

    return (
        <div className="wf-node text-center" style={{ width: 140 }}>
            <Handle type="target" position={Position.Left} style={{ top: 40, opacity: 0 }} />
            <div className="position-relative d-inline-block">
                <div
                    className="rounded-circle d-flex align-items-center justify-content-center text-white shadow"
                    style={{ width: 80, height: 80, background: color, border: `3px solid ${ring}`, cursor: 'pointer' }}
                >
                    <i className={moduleIcon(definition?.icon) + " fs-1"} />
                </div>
                {/* The theme's text colour on its card colour: dark on light, light on dark (bg-dark
                    turns light grey in the dark theme, under white text). */}
                <span
                    className="position-absolute top-0 start-0 badge rounded-pill"
                    style={{ fontSize: 11, minWidth: 22, background: 'var(--vz-body-color)', color: 'var(--vz-card-bg-custom)', border: '2px solid var(--vz-card-bg-custom)' }}
                >{module.id}</span>
                {run &&
                    // Clicks reach the editor through ReactFlow's onNodeClick (a plain
                    // onClick loses to the drag handling); data-run marks this target.
                    <span
                        role="button"
                        data-run="1"
                        className="position-absolute top-0 end-0 badge rounded-pill"
                        style={{ background: RUN_COLORS[run.status], fontSize: 11 }}
                        title={run.status}
                    >{run.operations}</span>
                }
            </div>
            <div className="mt-1 fw-semibold small text-truncate" title={definition?.label || module.module}>
                {definition?.label || module.module}
            </div>
            <div className="text-muted text-truncate" style={{ fontSize: 10 }}>{module.module}</div>
            <Handle type="source" position={Position.Right} style={{ top: 40, opacity: 0 }} />
        </div>
    );
};

export default memo(ModuleNode);
